import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { setToastRef } from '../utils/toast';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, duration?: number, action?: { label: string; onClick: () => void }) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', duration?: number, action?: { label: string; onClick: () => void }) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      
      // Set default duration based on type if not provided
      let finalDuration = duration;
      if (duration === undefined) {
        // Info toasts stay indefinitely (0), others auto-dismiss after 3s
        finalDuration = type === 'info' ? 0 : 3000;
      }
      
      const newToast: Toast = { id, message, type, duration: finalDuration, action };

      setToasts((prev) => [...prev, newToast]);
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, duration?: number) => showToast(message, 'success', duration ?? 3000),
    [showToast]
  );

  const error = useCallback(
    (message: string, duration?: number) => showToast(message, 'error', duration ?? 3000),
    [showToast]
  );

  const warning = useCallback(
    (message: string, duration?: number) => showToast(message, 'warning', duration ?? 3000),
    [showToast]
  );

  const info = useCallback(
    (message: string, duration?: number) => showToast(message, 'info', duration ?? 0),
    [showToast]
  );

  const contextValue = {
    toasts,
    showToast,
    success,
    error,
    warning,
    info,
    removeToast,
  };

  // Set the global toast reference for non-hook usage
  useEffect(() => {
    setToastRef(contextValue);
  }, [contextValue]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};
